import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import client from "./api/client";
import { toastError } from "./toastHelper";

const RELATIVE_KEY = "__relative__";

const SELECTED_GROUP = { key: "__selected__", label: "Selected", relative: false };

let pickerInstanceSeq = 0;

const CATEGORY_ICONS = {
  smart: "fa-sparkles",
  events: "fa-calendar",
  custom: "fa-filter",
};

const TOKENS = {
  bg: "var(--sl-bg-primary, #fff)",
  hover: "var(--sl-bg-secondary-hover, #f5f5f5)",
  border: "var(--sl-border-primary, #d5d7da)",
  text: "var(--sl-text-primary, #181d27)",
  muted: "var(--sl-text-secondary, #535862)",
  tertiary: "var(--sl-text-tertiary, #a4a7ae)",
  placeholder: "var(--sl-text-placeholder, #717680)",
  brand: "var(--sl-text-brand, #136395)",
  brandBg: "var(--sl-bg-brand-primary, #eff4ff)",
};

const formatCount = (count) => {
  if (count === null || count === undefined) return null;
  const number = Number(count);
  if (Number.isNaN(number)) return null;
  return number.toLocaleString();
};

const sameValue = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

const buttonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "8px 12px",
  borderRadius: 8,
  border: `1px solid ${TOKENS.border}`,
  backgroundColor: TOKENS.bg,
  color: TOKENS.text,
  fontSize: 14,
  cursor: "pointer",
  textAlign: "left",
};

const POPOVER_GAP = 4;
// The menu is a two-pane layout (category rail + segment list); the trigger is
// often much narrower than that needs, so floor the popover at a width that
// fits both panes rather than inheriting the trigger width.
const POPOVER_MIN_WIDTH = 520;
const POPOVER_MAX_HEIGHT = 440;
const VIEWPORT_BOTTOM_MARGIN = 24;
const MIN_POPOVER_HEIGHT = 200;

const popoverBaseStyle = {
  position: "fixed",
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  borderRadius: 8,
  border: `1px solid ${TOKENS.border}`,
  backgroundColor: TOKENS.bg,
  boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
  overflow: "hidden",
};

const searchWrapStyle = {
  position: "relative",
  padding: 8,
  borderBottom: `1px solid ${TOKENS.border}`,
  flexShrink: 0,
};

const searchInputStyle = {
  width: "100%",
  padding: "8px 12px 8px 34px",
  borderRadius: 8,
  border: `1px solid ${TOKENS.border}`,
  backgroundColor: TOKENS.bg,
  color: TOKENS.text,
  fontSize: 14,
};

const searchIconStyle = {
  position: "absolute",
  left: 20,
  top: "50%",
  transform: "translateY(-50%)",
  color: TOKENS.placeholder,
  fontSize: 13,
  pointerEvents: "none",
};

const panesStyle = {
  display: "flex",
  alignItems: "stretch",
  flex: 1,
  minHeight: 0,
};

const railStyle = {
  width: 200,
  flexShrink: 0,
  borderRight: `1px solid ${TOKENS.border}`,
  padding: 6,
  overflowY: "auto",
};

const stackedPanesStyle = { ...panesStyle, flexDirection: "column" };

const pillRailStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  padding: 6,
  borderBottom: `1px solid ${TOKENS.border}`,
  flexShrink: 0,
};

const triggerLineStyle = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const optionsPaneStyle = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};

const optionsHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "10px 12px 6px",
  flexShrink: 0,
};

const headerLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.04em",
  color: TOKENS.tertiary,
  textTransform: "uppercase",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const toggleStyle = {
  border: "none",
  background: "transparent",
  color: TOKENS.brand,
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  padding: 0,
  whiteSpace: "nowrap",
};

const optionsListStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: "0 6px 6px",
};

const railRowStyle = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "none",
  backgroundColor: active ? TOKENS.hover : "transparent",
  color: active ? TOKENS.brand : TOKENS.text,
  fontSize: 14,
  cursor: "pointer",
  textAlign: "left",
});

const countBadgeStyle = (active) => ({
  marginLeft: "auto",
  flexShrink: 0,
  minWidth: 20,
  padding: "1px 7px",
  borderRadius: 999,
  fontSize: 12,
  textAlign: "center",
  backgroundColor: active ? TOKENS.brandBg : TOKENS.hover,
  color: active ? TOKENS.brand : TOKENS.muted,
});

const optionRowStyle = (selected) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "none",
  backgroundColor: selected ? TOKENS.brandBg : "transparent",
  color: TOKENS.text,
  fontSize: 14,
  cursor: "pointer",
  textAlign: "left",
});

const optionNameStyle = {
  color: TOKENS.text,
  fontSize: 14,
  lineHeight: 1.3,
  overflowWrap: "anywhere",
};

const optionMetaStyle = {
  color: TOKENS.muted,
  fontSize: 12,
  lineHeight: 1.3,
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const messageStyle = {
  padding: "16px 12px",
  color: TOKENS.muted,
  fontSize: 14,
};

export default function SegmentPicker({
  team,
  value,
  onChange,
  relativeGroup,
  selectedLabel,
  selectedType,
  matchTriggerWidth = false,
  placeholder = "Select a segment",
  multiple = false,
  excludeIds = [],
  selectedOptions = [],
  invalid = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [serverGroups, setServerGroups] = useState([]);
  const [activeCategoryKey, setActiveCategoryKey] = useState(null);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolvedQuery, setResolvedQuery] = useState(null);
  const [popoverRect, setPopoverRect] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (process.env.NODE_ENV !== "production") {
    if (multiple && value && value.segmentId != null) {
      console.error("SegmentPicker: `multiple` reads value.segmentIds — value.segmentId is ignored");
    }
    if (multiple && relativeGroup) {
      console.error("SegmentPicker: `multiple` does not support relativeGroup");
    }
  }

  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) instanceIdRef.current = ++pickerInstanceSeq;

  const listboxId = `segment-picker-listbox-${instanceIdRef.current}`;
  const optionDomId = (index) => `segment-picker-option-${instanceIdRef.current}-${index}`;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updatePopoverPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP - VIEWPORT_BOTTOM_MARGIN;
    const spaceAbove = rect.top - POPOVER_GAP - VIEWPORT_BOTTOM_MARGIN;
    const openUp = spaceBelow < MIN_POPOVER_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.min(POPOVER_MAX_HEIGHT, Math.max(MIN_POPOVER_HEIGHT, openUp ? spaceAbove : spaceBelow));
    // Floor the width at POPOVER_MIN_WIDTH (capped to the viewport), then clamp
    // left so a widened popover never runs off the right edge — matters where
    // the trigger sits in a narrow sidebar (custom-campaign email/SMS nodes).
    const width = matchTriggerWidth
      ? rect.width
      : Math.max(rect.width, Math.min(POPOVER_MIN_WIDTH, window.innerWidth - 2 * POPOVER_GAP));
    const left = Math.max(POPOVER_GAP, Math.min(rect.left, window.innerWidth - width - POPOVER_GAP));
    setPopoverRect({
      left,
      width,
      maxHeight,
      top: openUp ? null : rect.bottom + POPOVER_GAP,
      bottom: openUp ? window.innerHeight - rect.top + POPOVER_GAP : null,
    });
  }, [matchTriggerWidth]);

  const groups = useMemo(() => {
    if (relativeGroup) {
      return [
        {
          key: RELATIVE_KEY,
          label: relativeGroup.label,
          relative: true,
          options: relativeGroup.options || [],
        },
        ...serverGroups,
      ];
    }
    return serverGroups;
  }, [relativeGroup, serverGroups]);

  const totalCount = useMemo(
    () => serverGroups.reduce((sum, group) => sum + (Number(group.count) || 0), 0),
    [serverGroups]
  );

  const excludeParam = (excludeIds || []).join(",");

  const fetchGroups = useCallback(
    (term) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      const params = { q: term || "", hide_empty: hideEmpty };
      if (excludeParam) params.exclude = excludeParam;
      client
        .get(`/teams/${team.id}/segments/picker.json`, { params })
        .then((response) => {
          if (!isMountedRef.current || requestIdRef.current !== requestId) return;
          setServerGroups(Array.isArray(response.data) ? response.data : []);
          setResolvedQuery(term || "");
          setLoading(false);
        })
        .catch(() => {
          if (!isMountedRef.current || requestIdRef.current !== requestId) return;
          setServerGroups([]);
          setLoading(false);
          toastError("Could not load segments, please try again");
        });
    },
    [team.id, hideEmpty, excludeParam]
  );

  const debouncedFetch = useMemo(() => _.debounce(fetchGroups, 300), [fetchGroups]);

  useEffect(() => {
    if (!open) return undefined;
    if (query === "") {
      fetchGroups("");
      return undefined;
    }
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [open, query, fetchGroups, debouncedFetch]);

  useEffect(() => {
    if (!open) return;
    setActiveCategoryKey((current) => {
      if (current && groups.some((group) => group.key === current)) return current;
      if (relativeGroup) return RELATIVE_KEY;
      const firstNonEmpty = groups.find((group) => (group.options || []).length > 0);
      if (firstNonEmpty) return firstNonEmpty.key;
      return groups[0] ? groups[0].key : null;
    });
  }, [groups, open, relativeGroup]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      const insideButton = buttonRef.current && buttonRef.current.contains(event.target);
      const insidePopover = popoverRef.current && popoverRef.current.contains(event.target);
      if (!insideButton && !insidePopover) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const handleFocusOut = (event) => {
      const next = event.relatedTarget;
      if (!next) return;
      const insideButton = buttonRef.current && buttonRef.current.contains(next);
      const insidePopover = popoverRef.current && popoverRef.current.contains(next);
      if (!insideButton && !insidePopover) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    document.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", updatePopoverPosition, true);
    window.addEventListener("resize", updatePopoverPosition);
    return () => {
      window.removeEventListener("scroll", updatePopoverPosition, true);
      window.removeEventListener("resize", updatePopoverPosition);
    };
  }, [open, updatePopoverPosition]);

  const handleToggle = () => {
    const next = !open;
    if (next) {
      setQuery("");
      updatePopoverPosition();
    }
    setOpen(next);
  };

  const handleSelect = (group, option) => {
    if (group.relative) {
      onChange({ segmentName: option.value });
    } else if (multiple) {
      onChange({
        segmentId: option.value,
        label: option.canonical_label || option.label,
        selected: !isSelected(group, option),
      });
      return;
    } else {
      onChange({ segmentId: option.value, label: option.label });
    }
    setOpen(false);
    buttonRef.current?.focus();
  };

  const handleRailClick = (key) => {
    setQuery("");
    setActiveCategoryKey(key);
  };

  const isSelected = (group, option) => {
    if (group.relative) return sameValue(value && value.segmentName, option.value);
    if (multiple) return ((value && value.segmentIds) || []).some((id) => sameValue(id, option.value));
    return sameValue(value && value.segmentId, option.value);
  };

  const searchMode = query.trim() !== "";
  const activeGroup = groups.find((group) => group.key === activeCategoryKey) || null;

  const rows = useMemo(() => {
    let base;
    if (searchMode) {
      base = serverGroups.flatMap((group) =>
        (group.options || []).map((option) => ({ group, option }))
      );
    } else if (activeGroup) {
      base = (activeGroup.options || []).map((option) => ({ group: activeGroup, option }));
    } else {
      base = [];
    }

    // A selected option the server no longer lists (discarded segment, beyond
    // the per-category limit, or hidden by "Hide empty") would otherwise be
    // impossible to deselect — pin it so toggle-off always works.
    if (multiple && selectedOptions.length > 0) {
      const listedValues = new Set(
        serverGroups.flatMap((group) => (group.options || []).map((option) => String(option.value)))
      );
      const unlisted = selectedOptions.filter((option) => !listedValues.has(String(option.value)));
      if (unlisted.length > 0) {
        base = unlisted.map((option) => ({ group: SELECTED_GROUP, option })).concat(base);
      }
    }

    return base;
  }, [searchMode, serverGroups, activeGroup, multiple, selectedOptions]);

  // A debounced fetch for the current query/hide_empty combo may still be in
  // flight (or not yet fired): `rows` can still hold the previous query's
  // results, so keyboard selection and aria-activedescendant must not act on
  // or point at them until they settle.
  const stale = loading || resolvedQuery !== query;

  useEffect(() => {
    setActiveIndex(0);
  }, [query, activeCategoryKey, hideEmpty]);

  useEffect(() => {
    setActiveIndex((index) => (index > rows.length - 1 ? Math.max(0, rows.length - 1) : index));
  }, [rows.length]);

  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`segment-picker-option-${instanceIdRef.current}-${activeIndex}`);
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, rows.length]);

  const handleListKeyDown = (event) => {
    if (stale) return;
    if (rows.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(rows.length - 1, index + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(0, index - 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(rows.length - 1);
    } else if (event.key === "Enter") {
      const row = rows[activeIndex];
      if (row) {
        event.preventDefault();
        handleSelect(row.group, row.option);
      }
    }
  };

  const buildMeta = (group, option) => {
    if (group.relative) return null;
    const fansText = option.count == null ? null : (Number(option.count) > 0 ? `${formatCount(option.count)} fans` : "No fans yet");
    return [searchMode ? group.label : null, option.date, fansText].filter(Boolean).join(" · ");
  };

  const showToggle = searchMode || (activeGroup && !activeGroup.relative);
  const headerLabel = searchMode
    ? "All segments"
    : activeGroup
    ? activeGroup.label
    : "";

  const buttonLabel = selectedLabel && selectedLabel.length > 0 ? selectedLabel : placeholder;
  const isPlaceholder = !(selectedLabel && selectedLabel.length > 0);

  const popover =
    open && popoverRect ? (
      <div
        ref={popoverRef}
        style={{
          ...popoverBaseStyle,
          left: popoverRect.left,
          width: popoverRect.width,
          maxHeight: popoverRect.maxHeight,
          ...(popoverRect.top != null ? { top: popoverRect.top } : { bottom: popoverRect.bottom }),
        }}
      >
        <div style={searchWrapStyle}>
          <i className="far fa-magnifying-glass" style={searchIconStyle} />
          <input
            type="text"
            style={searchInputStyle}
            placeholder={`Search all ${formatCount(totalCount)} segments…`}
            aria-label="Search segments"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={!stale && rows.length > 0 ? optionDomId(activeIndex) : undefined}
            value={query}
            autoFocus
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleListKeyDown}
          />
        </div>

        <div style={matchTriggerWidth ? stackedPanesStyle : panesStyle}>
          <div style={matchTriggerWidth ? pillRailStyle : railStyle}>
            {groups.map((group) => {
              const active = group.key === activeCategoryKey;
              const icon = group.relative ? null : CATEGORY_ICONS[group.key];
              return (
                <button
                  key={group.key}
                  type="button"
                  style={{ ...railRowStyle(active), ...(matchTriggerWidth ? { width: "auto", padding: "4px 10px" } : {}) }}
                  onClick={() => handleRailClick(group.key)}
                >
                  {icon ? <i className={`far ${icon}`} style={{ width: 16, flexShrink: 0, textAlign: "center" }} /> : null}
                  <span>{group.label}</span>
                  {group.relative ? null : (
                    <span style={countBadgeStyle(active)}>{formatCount(group.count || 0)}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div style={optionsPaneStyle}>
            <div style={optionsHeaderStyle}>
              <span style={headerLabelStyle}>{headerLabel}</span>
              {showToggle ? (
                <button type="button" style={toggleStyle} onClick={() => setHideEmpty((prev) => !prev)}>
                  {hideEmpty ? "Show empty" : "Hide empty"}
                </button>
              ) : null}
            </div>

            <div style={optionsListStyle} id={listboxId} role="listbox" aria-label="Segments" aria-multiselectable={multiple || undefined}>
              {loading ? (
                <div style={messageStyle}>Loading…</div>
              ) : rows.length === 0 ? (
                <div style={messageStyle}>No segments match</div>
              ) : (
                rows.map(({ group, option }, index) => {
                  const selected = isSelected(group, option);
                  const meta = buildMeta(group, option);
                  const empty = !group.relative && option.count != null && !(Number(option.count) > 0);
                  const active = index === activeIndex;
                  return (
                    <button
                      key={`${group.key}:${option.value}`}
                      id={optionDomId(index)}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      style={{ ...optionRowStyle(selected), backgroundColor: selected ? TOKENS.brandBg : active ? TOKENS.hover : "transparent" }}
                      onClick={() => handleSelect(group, option)}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <span style={{ flex: 1, minWidth: 0, opacity: empty ? 0.55 : 1 }}>
                        <span style={optionNameStyle}>{option.label}</span>
                        {meta ? <span style={{ ...optionMetaStyle, display: "block" }}>{meta}</span> : null}
                      </span>
                      {selected ? (
                        <i className="far fa-check" style={{ marginLeft: 8, flexShrink: 0, color: TOKENS.brand, fontSize: 13 }} />
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        ref={buttonRef}
        style={invalid ? { ...buttonStyle, borderColor: "var(--sl-border-error, #FDA29B)" } : buttonStyle}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
      >
        {selectedType && !isPlaceholder ? (
          <span style={{ flex: 1, minWidth: 0 }} title={`${selectedType} · ${buttonLabel}`}>
            <span data-trigger-line style={{ ...triggerLineStyle, fontSize: 12, color: TOKENS.muted }}>{selectedType}</span>
            <span data-trigger-line style={{ ...triggerLineStyle, fontSize: 14, color: TOKENS.text }}>{buttonLabel}</span>
          </span>
        ) : (
          <span
            title={isPlaceholder ? undefined : buttonLabel}
            style={{ ...triggerLineStyle, flex: 1, minWidth: 0, color: isPlaceholder ? TOKENS.placeholder : TOKENS.text }}
          >
            {buttonLabel}
          </span>
        )}
        <i className="far fa-chevron-down" style={{ marginLeft: 8, color: TOKENS.muted, fontSize: 12, flexShrink: 0 }} />
      </button>

      {popover ? ReactDOM.createPortal(popover, document.body) : null}
    </div>
  );
}

SegmentPicker.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  value: PropTypes.shape({
    segmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    segmentIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    segmentName: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  excludeIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
    })
  ),
  invalid: PropTypes.bool,
  relativeGroup: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        label: PropTypes.string,
      })
    ),
  }),
  selectedLabel: PropTypes.string,
  selectedType: PropTypes.string,
  matchTriggerWidth: PropTypes.bool,
  placeholder: PropTypes.string,
};
